import React from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import bgRight from 'assets/images/bg_right.png';
import PodiumCard from './PodiumCard';
import { heroAvatar } from './constants';

const RightPredictionPanel = ({
  title,
  podiumData,
  hideCountryOnCard,
  searchText,
  isSearchFocused,
  suggestionList,
  canVote,
  searchError,
  onPrevPodium,
  onNextPodium,
  onSelectRank,
  onSearchFocus,
  onSearchBlur,
  onSearchChange,
  onSearchEnter,
  onSelectSuggestion,
  onOpenVotePopup
}) => {
  return (
    <div
      className="order-1 md:order-2 rounded-[20px] border-[2px] border-[#11833c] ring-[3px] ring-[#11833c] p-2 md:p-3 bg-no-repeat bg-center bg-cover"
      style={{ backgroundImage: `url(${bgRight})` }}
    >
      <div className="relative overflow-visible rounded-[18px] border-[2px] border-[#3c9a66] p-3 md:p-4">
        <div className="mx-auto -mt-[2px] mb-[8px] w-fit rounded-[10px] bg-gradient-to-b from-[#006719] to-[#2CF65D] p-[2px] md:-mt-[4px] md:rounded-[12px]">
          <div className="inline-flex h-[40px] items-center justify-center rounded-[9px] bg-gradient-to-b from-[#00CD32] to-[#048724] px-4 text-center text-[14px] font-bold uppercase text-white md:h-[48px] md:rounded-[11px] md:px-6 md:text-[34px]">
            {title}
          </div>
        </div>

        <div className="relative grid grid-cols-[28px_1fr_28px] items-end gap-0 md:grid-cols-[148px_1fr_148px] md:gap-0">
          <button
            type="button"
            onClick={onPrevPodium}
            className="cursor-pointer absolute left-0 top-1/2 z-[5] hidden h-[32px] w-[32px] -translate-x-[2px] -translate-y-1/2 items-center justify-center rounded-full border-none bg-white/95 text-[28px] leading-none text-[#007524] shadow-[0_2px_8px_rgba(0,0,0,0.2)] outline-none ring-0 appearance-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 md:flex md:h-[44px] md:w-[44px] md:-translate-x-[10px] md:text-[38px]"
          >
            <ChevronLeft size={34} strokeWidth={2.5} />
          </button>

          <button
            type="button"
            onClick={() => onSelectRank(podiumData.left?.index ?? 0)}
            className="border-none bg-transparent p-0 m-0 appearance-none cursor-pointer"
          >
            <PodiumCard
              name={podiumData.left?.name || 'Unknown'}
              country={podiumData.left?.teamName || ''}
              avatar={podiumData.left?.avatar || heroAvatar}
              zoomAvatar={hideCountryOnCard}
              hideCountry={hideCountryOnCard}
              className="opacity-85 scale-[0.92] se:-translate-x-[10px] translate-x-[20px] md:translate-x-[72px]"
            />
          </button>

          <button
            type="button"
            onClick={() => onSelectRank(podiumData.center?.index ?? 0)}
            className="relative z-[3] border-none bg-transparent p-0 m-0 appearance-none cursor-pointer pointer-events-none md:pointer-events-auto"
          >
            <PodiumCard
              large
              name={podiumData.center?.name || 'Unknown'}
              country={podiumData.center?.teamName || ''}
              avatar={podiumData.center?.avatar || heroAvatar}
              zoomAvatar={hideCountryOnCard}
              hideCountry={hideCountryOnCard}
            />
          </button>

          <button
            type="button"
            onClick={() => onSelectRank(podiumData.right?.index ?? 0)}
            className="relative z-[1] border-none bg-transparent p-0 m-0 appearance-none cursor-pointer"
          >
            <PodiumCard
              name={podiumData.right?.name || 'Unknown'}
              country={podiumData.right?.teamName || ''}
              avatar={podiumData.right?.avatar || heroAvatar}
              zoomAvatar={hideCountryOnCard}
              hideCountry={hideCountryOnCard}
              className="opacity-85 scale-[0.92] se:-translate-x-[52px] -translate-x-[82px] md:-translate-x-[82px]"
            />
          </button>

          <button
            type="button"
            onClick={onNextPodium}
            className="cursor-pointer absolute right-0 top-1/2 z-[5] hidden h-[32px] w-[32px] translate-x-[2px] -translate-y-1/2 items-center justify-center rounded-full border-none bg-white/95 text-[28px] leading-none text-[#007524] shadow-[0_2px_8px_rgba(0,0,0,0.2)] outline-none ring-0 appearance-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 md:flex md:h-[44px] md:w-[44px] md:translate-x-[10px] md:text-[38px]"
          >
            <ChevronRight size={34} strokeWidth={2.5} />
          </button>
        </div>

        <div className="mx-auto mt-2 flex w-full max-w-[430px] flex-col items-center gap-[6px] rounded-[14px] bg-transparent p-0 md:mt-4 md:flex-row md:gap-[8px] md:rounded-full md:bg-[#007a28] md:p-[4px]">
          <div className="relative w-full flex-1">
            <input
              type="text"
              placeholder={hideCountryOnCard ? 'Tìm tên đội của bạn...' : 'Tìm tên cầu thủ của bạn...'}
              value={searchText}
              onFocus={onSearchFocus}
              onClick={onSearchFocus}
              onBlur={onSearchBlur}
              onChange={onSearchChange}
              onKeyDown={onSearchEnter}
              className="h-[34px] w-full rounded-full border-0 bg-white pl-3 pr-9 text-[12px] text-[#6e6e6e] outline-none md:text-[13px]"
            />
            <SearchOutlined className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[18px] text-[#666]" />

            {isSearchFocused ? (
              <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-[30] overflow-hidden rounded-[14px] bg-[#f2f4f7] shadow-[0_10px_24px_rgba(0,0,0,0.2)]">
                <div className="scrollbar-hide max-h-[260px] overflow-y-auto py-1">
                  {suggestionList.length > 0 ? (
                    suggestionList.map((item) => (
                      <button
                        key={`suggestion-${item.index}`}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          onSelectSuggestion(item);
                        }}
                        className="block w-full border-0 bg-transparent px-4 py-2 text-left outline-none ring-0 shadow-none appearance-none hover:bg-[#e7eaef] focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                      >
                        <div className="text-[16px] font-semibold text-[#2f3b4a]">{item.name}</div>
                        {!hideCountryOnCard && item.teamName ? (
                          <div className="mt-0.5 text-[13px] font-medium text-[#758195]">{item.teamName}</div>
                        ) : null}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-[14px] text-[#6b7280]">Không có gợi ý phù hợp</div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={onOpenVotePopup}
            disabled={!canVote}
            className="border-none flex h-[30px] min-w-[98px] items-center justify-center rounded-full bg-[#efefef] px-4 text-[16px] font-semibold text-[#3b3b3b] shadow-[inset_0_0_0_1px_#d2d2d2] md:h-[34px] md:px-3 md:text-[12px] md:font-bold disabled:cursor-not-allowed disabled:opacity-60"
          >
            Bình Chọn
          </button>
        </div>

        {searchError ? (
          <p className="mt-1 text-center text-[12px] font-semibold text-[#ff4d4f]">{searchError}</p>
        ) : null}
      </div>
    </div>
  );
};

export default RightPredictionPanel;
